import { Box, Paper, Typography, useTheme } from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LuChevronRight } from "react-icons/lu";
import foot from "../../../assets/logo/foot.png";
import { getOrgTokens } from "../../../theme/style/organizationPalette";
import DppPhotoGallery from "./DppPhotoGallery";
import { dppSectionList as sectionList } from "./dppPassportSections";

const GREEN = "#659d33";

const useCreateTok = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  return { t, theme, isDark, tok };
};

/**
 * Editable replica of ProductDPP_Preview's sidebar + content-pane layout.
 * Field rendering is delegated to the parent's `dppControl` (the same
 * TextBox/CheckBox/list editor used by the rest of the edit flow) so
 * every field here stays fully user-editable.
 */
const ProductDPP_CreateForm = ({ dpp, dppControl, entityId, qrValue, idColumnName = "product_id", renameLabel = (s) => s, photoRefreshSignal, onPhotosChanged, nameField = "product_name" }) => {
  const { t, tok, isDark } = useCreateTok();
  const [selected, setSelected] = useState(sectionList[0].key);

  const active = sectionList.find((s) => s.key === selected) || sectionList[0];

  const renderActiveContent = () => {
    if (active.key === "photo") {
      return <DppPhotoGallery entityId={entityId} idColumnName={idColumnName} scope="DPP Evidence" refreshSignal={photoRefreshSignal} onPhotosChanged={onPhotosChanged} />;
    }
    if (active.key === "hosting") {
      return (
        <>
          {dppControl(active.dppSection)}
          {dpp?._id && (
            <Box sx={{ mt: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
              <QRCodeCanvas
                value={qrValue}
                size={200}
                bgColor="#ffffff"
                fgColor="#000000"
                level="H"
                marginSize={10}
                imageSettings={{
                  src: foot,
                  x: undefined,
                  y: undefined,
                  height: 22,
                  width: 22,
                  opacity: 1,
                  excavate: true,
                }}
              />
            </Box>
          )}
        </>
      );
    }
    return dppControl(active.dppSection);
  };

  return (
    <Box sx={{ display: "grid", gap: 2, textAlign: "left" }}>
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "320px 1fr" }, alignItems: "start" }}>
        {/* ── Left nav ── */}
        <Paper sx={{ border: 1, borderColor: tok.border.card, borderRadius: tok.radius.card, overflow: "hidden", alignSelf: "start", p: 2 }}>
          <Typography sx={{ mb: 1.5, fontWeight: 600, fontSize: "15px", color: tok.text.primary, fontFamily: tok.font.family, textAlign: "left" }}>
            {t("Passport Contents")}
          </Typography>
          <Box component="ol" sx={{ display: "grid", gap: 0.75, m: 0, p: 0, listStyle: "none" }}>
            {sectionList.map((s, i) => {
              const Icon = s.icon;
              const isActive = s.key === selected;
              return (
                <Box component="li" key={s.key} sx={{ listStyle: "none" }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setSelected(s.key)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.25, width: "100%", textAlign: "left",
                      border: `1px solid ${isActive ? "rgba(101,157,51,0.4)" : tok.border.card}`,
                      backgroundColor: isActive ? (isDark ? "rgba(101,157,51,0.15)" : "rgba(101,157,51,0.10)") : "transparent",
                      borderRadius: "6px", px: 1.5, py: 1, cursor: "pointer", fontFamily: tok.font.family,
                      "&:hover": { backgroundColor: isActive ? undefined : (isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)") },
                    }}
                  >
                    <Box sx={{ width: 24, height: 24, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: "11px", fontWeight: 600, flexShrink: 0, backgroundColor: isActive ? GREEN : (isDark ? "rgba(101,157,51,0.15)" : "rgba(101,157,51,0.12)"), color: isActive ? "#fff" : GREEN }}>
                      {i + 1}
                    </Box>
                    <Icon size={16} color={GREEN} style={{ flexShrink: 0 }} />
                    <Typography sx={{ flex: 1, fontSize: tok.font.size.bodyText, color: tok.text.primary, fontFamily: tok.font.family, textAlign: "left" }}>{t(renameLabel(s.label))}</Typography>
                    <LuChevronRight size={16} color={isActive ? GREEN : tok.text.muted} style={{ flexShrink: 0 }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>

        {/* ── Content ── */}
        <Paper sx={{ border: 1, borderColor: tok.border.card, borderRadius: tok.radius.card, p: 3 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5, mb: 2, mx: -3, px: 3, pb: 1.5, borderBottom: `1px solid ${tok.border.card}` }}>
            <Box sx={{ textAlign: "left" }}>
              <Typography sx={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.08em", color: GREEN, fontFamily: tok.font.family, textAlign: "left" }}>
                {t(renameLabel("Digital Product Passport"))} • {t("Create")}
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: "20px", fontWeight: 600, color: tok.text.primary, fontFamily: tok.font.family, textAlign: "left" }}>{t(renameLabel(active.label))}</Typography>
              <Typography sx={{ fontSize: tok.font.size.bodyText, color: tok.text.muted, fontFamily: tok.font.family, textAlign: "left" }}>{t(renameLabel(active.blurb))}</Typography>
            </Box>
            {dpp?.[nameField] && (
              <Box sx={{ textAlign: "right" }}>
                <Typography sx={{ fontWeight: 600, color: tok.text.primary, fontFamily: tok.font.family, textAlign: "right" }}>{t(renameLabel("Product"))}</Typography>
                <Typography sx={{ fontSize: tok.font.size.caption, color: tok.text.muted, fontFamily: tok.font.family, textAlign: "right" }}>{dpp[nameField]}</Typography>
              </Box>
            )}
          </Box>
          {renderActiveContent()}
        </Paper>
      </Box>
    </Box>
  );
};
export default ProductDPP_CreateForm;
